import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { WorkOrderId } from "../lib/workIds";
import {
  newerProcedureExecution,
  type ProcedureExecution,
  ProcedureItemConflictError,
  procedureExecutionQueryKey,
  type SaveProcedureItemInput,
  saveProcedureItem,
} from "../queries/useProcedureExecution";

/**
 * Saves one Procedure item as the user works. Field autosave shows its result
 * inline on the item ("Saving", "Saved") instead of a success toast, because a
 * toast on every blur would be noise. A failed save still raises an error
 * toast; a conflict does not, because the item itself offers the choice.
 */
export function useSaveProcedureItemMutation(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  const queryKey = procedureExecutionQueryKey(
    selectedCustomer?.id,
    selectedFacility?.id,
    workOrderId,
  );

  return useMutation({
    mutationFn: async (input: SaveProcedureItemInput & { itemId: string }) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const { itemId, ...body } = input;
      try {
        return await saveProcedureItem(
          {
            customerId: selectedCustomer.id,
            facilityId: selectedFacility.id,
            workOrderId,
          },
          itemId,
          body,
        );
      } catch (error) {
        if (!(error instanceof ProcedureItemConflictError)) throw error;
        // Reload the whole snapshot instead of patching one item into the
        // cache: a patched cache mixes states the server never had, so a later
        // reply could not be ordered against it. The conflict is reported only
        // after the reload, so it is never shown, or resolved, against a cache
        // that predates it. A failed reload is reported as a failed, retryable
        // save rather than as a conflict.
        try {
          await queryClient.refetchQueries(
            { queryKey, exact: true },
            { throwOnError: true },
          );
        } catch {
          throw new Error(
            "Someone else changed this answer. Retry to load it and try again.",
          );
        }
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData<ProcedureExecution>(queryKey, (cached) =>
        newerProcedureExecution(cached, data),
      );
      // Every stored change is a Work Order history event.
      void queryClient.invalidateQueries({ queryKey: ["work-order-detail"] });
    },
    onError: (error) => {
      // A conflict is shown on the item itself, which offers the choice.
      if (error instanceof ProcedureItemConflictError) return;
      toast.error("Unable to save your answer. Try again.", { icon: true });
    },
  });
}
